import { useEffect } from "react";
import { Box, styled } from "@mui/material";
import { CheckCircle, Lock, Shield, Zap } from "lucide-react";

const Overlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.92)",
  backdropFilter: "blur(8px)",
  padding: "16px",
});

const Card = styled(Box)({
  width: "320px",
  maxWidth: "100%",
  maxHeight: "90vh",
  overflowY: "auto",
  background: "#18181b",
  borderRadius: "12px",
  border: "1px solid rgba(255, 107, 53, 0.4)",
  boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
});

const Header = styled(Box)({
  background: "linear-gradient(135deg, rgba(255, 107, 53, 0.2), #18181b)",
  padding: "16px",
  textAlign: "center",
  borderBottom: "1px solid #27272a",
});

const Body = styled(Box)({
  padding: "14px 16px",
});

const FeatureGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "6px",
  marginBottom: "12px",
});

const FeatureItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "0.65rem",
  color: "#a1a1aa",
  fontFamily: "'Oswald', sans-serif",
  "& svg": { flexShrink: 0 },
});

const PriceBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "rgba(0,0,0,0.4)",
  borderRadius: "8px",
  padding: "10px 12px",
  marginBottom: "12px",
});

const CTAButton = styled("a")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  width: "100%",
  padding: "12px",
  background: "#FF6B35",
  color: "#fff",
  fontSize: "0.85rem",
  fontWeight: 700,
  fontFamily: "'Oswald', sans-serif",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  textDecoration: "none",
  borderRadius: "8px",
  boxShadow: "0 0 20px rgba(255, 107, 53, 0.4)",
  transition: "all 0.2s",
  "&:hover": {
    background: "#e55a2b",
    transform: "translateY(-1px)",
  },
});

const TrustRow = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "12px",
  marginTop: "10px",
  flexWrap: "wrap",
});

const TrustItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontSize: "0.6rem",
  color: "#52525b",
  fontFamily: "'Oswald', sans-serif",
});

const BackBtn = styled("button")({
  display: "block",
  width: "100%",
  marginTop: "8px",
  padding: "6px",
  fontSize: "0.6rem",
  color: "#52525b",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "'Oswald', sans-serif",
  "&:hover": { color: "#a1a1aa" },
});

const goalLabels = {
  strength: "Strength",
  hypertrophy: "Muscle",
  endurance: "Fat Loss",
  toned: "Recomp",
};

const levelLabels = {
  beginner: "Beginner",
  intermediate: "Intermediate", 
  advanced: "Advanced",
};

export default function PaywallPage({ quizData, onBack }) {
  const { goal, level, schedule, email } = quizData;

  useEffect(() => {
    window.gtag?.("event", "paywall_viewed", { goal, level, schedule });
    window.fbq?.("track", "ViewContent", { content_name: "Paywall" });
  }, [goal, level, schedule]);

  const stripeLink = process.env.REACT_APP_STRIPE_LINK_NEW || "https://buy.stripe.com/test_placeholder";
  const link = email
    ? `${stripeLink}${stripeLink.includes("?") ? "&" : "?"}prefilled_email=${encodeURIComponent(email)}`
    : stripeLink;

  const handleCTA = () => {
    window.gtag?.("event", "paywall_cta_clicked", { goal, level, schedule, price: 9.99 });
    window.fbq?.("track", "InitiateCheckout", { value: 9.99, currency: "USD" });
  };

  return (
    <Overlay>
      <Card>
        <Header>
          <div style={{ 
            width: 40, height: 40, borderRadius: "50%", 
            background: "rgba(34, 197, 94, 0.2)", 
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 10px"
          }}>
            <CheckCircle size={22} color="#22c55e" />
          </div>
          <div style={{ 
            fontSize: "1rem", fontWeight: 700, color: "#fff", 
            fontFamily: "'Oswald', sans-serif", textTransform: "uppercase" 
          }}>
            Your Program is Ready!
          </div>
          <div style={{ 
            fontSize: "0.7rem", color: "#a1a1aa", marginTop: "4px",
            fontFamily: "'Oswald', sans-serif" 
          }}>
            {levelLabels[level]} • {goalLabels[goal]} • {schedule} days/week
          </div>
        </Header>

        <Body>
          <div style={{ 
            fontSize: "0.6rem", color: "#71717a", marginBottom: "6px",
            fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", letterSpacing: "0.1em"
          }}>
            What's Included
          </div>
          
          <FeatureGrid>
            <FeatureItem><CheckCircle size={12} color="#22c55e" />4-Week Program</FeatureItem>
            <FeatureItem><CheckCircle size={12} color="#22c55e" />Progress Tracker</FeatureItem>
            <FeatureItem><CheckCircle size={12} color="#22c55e" />Warm-up Routines</FeatureItem>
            <FeatureItem><CheckCircle size={12} color="#22c55e" />Nutrition Guide</FeatureItem>
            <FeatureItem><CheckCircle size={12} color="#22c55e" />{parseInt(schedule) * 4} Workouts</FeatureItem>
            <FeatureItem><CheckCircle size={12} color="#22c55e" />Unlimited Regen</FeatureItem>
          </FeatureGrid>

          <PriceBox>
            <div>
              <div style={{ fontSize: "0.6rem", color: "#52525b", textDecoration: "line-through" }}>
                Personal trainer: $200
              </div>
              <div style={{ fontSize: "0.55rem", color: "#52525b" }}>One-time payment</div>
            </div>
            <div style={{ 
              fontSize: "1.4rem", fontWeight: 700, color: "#22c55e", 
              fontFamily: "'Oswald', sans-serif" 
            }}>
              $9.99
            </div>
          </PriceBox>

          <CTAButton href={link} onClick={handleCTA}>
            <Lock size={16} />
            Unlock My Program
          </CTAButton>

          <TrustRow>
            <TrustItem><Shield size={12} color="#22c55e" />Secure</TrustItem>
            <TrustItem><CheckCircle size={12} color="#22c55e" />30-day guarantee</TrustItem>
            <TrustItem><Zap size={12} color="#22c55e" />Instant</TrustItem>
          </TrustRow>

          <BackBtn onClick={onBack}>← Go back to quiz</BackBtn>
        </Body>
      </Card>
    </Overlay>
  );
}
